/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * @module enervee_utils.product_card
 */
ProductCardService.$inject = ['$q', '$filter', 'CmsContentService', 'UtilService'];
function ProductCardService($q, $filter, CmsContentService, UtilService) {
    var _pub = {
        getCardAriaText: getCardAriaText,
        setTextUpdatedCallback: setTextUpdatedCallback
    };

    _pub.textUpdatedCallbacks = {};

    function setTextUpdatedCallback (product_id, callback) {
        _pub.textUpdatedCallbacks[product_id] = callback;
    }

    /**
     * Helper function that generates the aria text for the entire card.
     *
     * @param {Object} product a product from the Swiftype endpoints
     * @param {Number} product.id
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Boolean} product.has_enervee_score
     * @param {Number} product.enervee_score
     * @param {Number} product.review_score
     * @param {Number} product.review_count
     * @param {String} product.display_name
     * @param {Number} product.price
     * @param {Object} [product.checkoutInfo]
     * @param {Number} [product.checkoutInfo.price]
     * @param {Number} [product.checkoutInfo.total_additional_discounts]
     * @param {Object} [product.rebates]
     * @param {Number} [product.rebate.amount]
     * @param {Boolean} product.hasPriceDrop
     * @param {Array<Object>} product.best_online_offers
     * @param {String} [product.rebateTagText]
     * @param {String} [product.noRebateText]
     * @param {Object} [product.rebateLite]
     * @param {String} [product.rebateLite.card_text]
     * @param {Array<Object>} [product.certificationBadges]
     * @param {String} [product.certificationBadges[].badge_text]
     * @return {Promise<String>}
     */
    function getCardAriaText(product) {
        var ariaCmsContext = {
            enervee_score: product.enervee_score,
            review_score: product.review_score,
            review_count: product.review_count,
        };

        // Product name
        $q.all([
            $q.resolve(product.display_name),
            getEnerveeScoreAriaText(product, ariaCmsContext),
            getPriceAriaText(product, ariaCmsContext),
            getPriceDropAriaText(product, ariaCmsContext),
            getOnlineOffersAriaText(product, ariaCmsContext),
            getRebateAriaTexts(product),
            getReviewAriaTexts(product, ariaCmsContext),
            getCertBadgeAriaText(product, ariaCmsContext),
        ]).then(function (cardAriaTexts) {
            cardAriaTexts = UtilService
                .flattenDeep(cardAriaTexts)
                .filter(function (text) {
                    return !!text;
                });

            cardAriaTexts = $filter('stripTags')(
                cardAriaTexts.join('; '),
                'strong, em, i, b, span, p, br'
            );

            if (_pub.textUpdatedCallbacks.hasOwnProperty(product.id) && typeof _pub.textUpdatedCallbacks[product.id] === 'function') {
                _pub.textUpdatedCallbacks[product.id](cardAriaTexts);
            }
        });
    }

    /**
     * Helper method to get the aria text for the enervee score
     *
     * @param {Object} product
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Boolean} product.has_enervee_score
     * @param {Object} context
     * @param {String} context.enervee_score
     * @return {Promise<String>}
     */
    function getEnerveeScoreAriaText(product, context) {
        if (product.has_enervee_score) {
            return CmsContentService.getMarketPlaceText(
                'product_card:enervee_score_sr',
                context,
                product.category.id
            );
        }
        return $q.resolve('');
    }

    /**
     * Helper method to get the aria text for the product price
     *
     * @param {Object} product
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Number} product.price
     * @param {Object} [product.checkoutInfo]
     * @param {Number} [product.checkoutInfo.price]
     * @param {Number} [product.checkoutInfo.total_additional_discounts]
     * @param {Object} [product.rebate]
     * @param {Number} [product.rebate.amount]
     * @param {Object} context
     * @return {Promise<String>}
     */
    function getPriceAriaText(product, context) {
        let price = product.price;
        if (product.checkoutInfo) {
            if (product.totalRebatesAmount) {
                context.price = $filter('toCurrency')(product.checkoutInfo.price - product.totalRebatesAmount);
                return CmsContentService.getMarketPlaceText(
                    'product_card:price_text',
                    context,
                    product.category.id
                );
            }
            price = product.checkoutInfo.price;
        }
        return $q.resolve($filter('toCurrency')(price));
    }

    /**
     * Helper method to get the aria text for the price drop
     *
     * @param {Object} product
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Boolean} product.hasPriceDrop
     * @param {Object} context
     * @return {Promise<String>}
     */
    function getPriceDropAriaText(product, context) {
        if (product.hasPriceDrop) {
            return CmsContentService.getMarketPlaceText(
                'product_card:price_drop_sr',
                context,
                product.category.id
            );
        }
        return $q.resolve('');
    }

    /**
     * Helper method to get the aria text for the enervee score
     *
     * @param {Object} product
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Array<Object>} product.best_online_offers
     * @param {Object} context
     * @return {Promise<String>}
     */
    function getOnlineOffersAriaText(product, context) {
        if (product.hasOwnProperty('best_online_offers') && product.best_online_offers.length > 0) {
            var offersPlural = '';
            if (product.best_online_offers.length > 1) {
                offersPlural = 's';
            }
            context.offersPlural = offersPlural;
            context.offers_count = product.best_online_offers.length;
            return CmsContentService.getMarketPlaceText(
                'product_card:online_offers_sr',
                context,
                product.category.id
            );
        }
        return $q.resolve('');
    }

    /**
     * Helper method to get the aria texts for rebates
     * NOTE: this method is best called after the rebates for the card are retrieved and the
     * rebate tag text is set.
     *
     * @param {Object} product
     * @param {String} [product.rebateTagText]
     * @param {String} [product.noRebateText]
     * @param {Object} [product.rebateLite]
     * @param {String} [product.rebateLite.card_text]
     * @return {Promise<Array<String>>}
     */
    function getRebateAriaTexts(product) {
        var rebateTexts = [];

        if (product.rebateTagText) {
            rebateTexts.push(
                product.rebateTagText
            );
        }

        // No rebate text?
        if (product.noRebateText) {
            rebateTexts.push(
                product.noRebateText
            );
        }
        // Rebate lite amount?
        if (product.rebateLite) {
            rebateTexts.push(
                product.rebateLite.card_text
            );
        }

        $q.resolve(rebateTexts);
    }

    /**
     * Helper method to get the aria texts for reviews
     *
     * @param {Object} product
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Number} product.review_count
     * @param {Object} context
     * @param {Number} context.review_count
     * @param {Number} context.review_score
     * @return {Promise<Array<String>>}
     */
    function getReviewAriaTexts(product, context) {
        if (product.review_count > 0) {
            return $q.all([
                CmsContentService.getMarketPlaceText(
                    'product_card:review_score_sr',
                    context,
                    product.category.id
                ),
                CmsContentService.getMarketPlaceText(
                    'product_card:review_count_sr',
                    context,
                    product.category.id
                ),
            ]);
        }
        return $q.resolve([]);
    }

    /**
     * Helper method to get the aria text for the cert badge
     *
     * @param {Object} product
     * @param {Object} product.category
     * @param {Number} product.category.id
     * @param {Array<Object>} [product.certificationBadges]
     * @param {String} [product.certificationBadges[].badge_text]
     * @param {Object} context
     * @return {Promise<String>}
     */
    function getCertBadgeAriaText(product, context) {
        if (product.hasOwnProperty('certificationBadges') && product.certificationBadges[0]) {
            context.badgeText = product.certificationBadges[0].badge_text;
            return CmsContentService.getMarketPlaceText(
                'product_card:cert_badge_sr',
                context,
                product.category.id
            );
        }
        return $q.resolve('');
    }

    return _pub;
}

module.exports = ProductCardService;
